import { z } from 'zod';
import { ToolRefWarningSchema } from './tool-ref.js';
// ─── WorkflowNode + Edge (the building blocks of a graph) ────────────────────
/**
 * `WorkflowNode.config` is node-type-specific; at save-time the API validates it
 * against the node type's own Zod schema (looked up in the runtime registry).
 * At the types package level we treat it as an arbitrary object.
 */
export const NodePositionSchema = z.object({ x: z.number(), y: z.number() }).nullable();
export const WorkflowNodeSchema = z.object({
    id: z.string(),
    workflowVersionId: z.string(),
    nodeKey: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[a-z][a-z0-9-]*$/, 'nodeKey must be kebab-case'),
    nodeType: z.string().min(1).max(100),
    config: z.record(z.string(), z.unknown()),
    inputMapping: z.record(z.string(), z.unknown()),
    outputSchema: z.record(z.string(), z.unknown()),
    position: NodePositionSchema.optional(),
});
export const WorkflowEdgeSchema = z.object({
    id: z.string(),
    workflowVersionId: z.string(),
    fromNodeKey: z.string(),
    toNodeKey: z.string(),
    /** JSONPath-ish expression evaluated against the from-node's output. */
    condition: z.string().nullable(),
    /**
     * Optional reference to a triage BoardColumn (per-org). When the runner
     * picks this edge during execution, the resulting Run is tagged with
     * the same column so the triage board's "outcome" view buckets it
     * accordingly. Null = edge has no outcome attribution. (DEV-16183.)
     */
    boardColumnId: z.string().nullable().optional(),
});
/**
 * Patch body for `PATCH /v1/workflows/:id/edges/:edgeId`. Mirrors
 * `patchNodeConfig`'s semantics — only the supplied fields are touched,
 * a new workflow version is cut to record the change. (DEV-16183.)
 *
 * Currently only `boardColumnId` is patchable through this endpoint;
 * editing `condition` still goes through the inline-on-edge UI which
 * already PATCHes the full graph. We'll widen this schema if/when a
 * dedicated condition editor lands in the edge inspector.
 */
export const PatchWorkflowEdgeInputSchema = z.object({
    /**
     * `null` clears the existing attribution. `undefined` (omitted) leaves
     * it unchanged. A string sets it. Server verifies the column belongs
     * to the requesting org before the version cut.
     */
    boardColumnId: z.string().nullable().optional(),
});
/**
 * One edge → BoardColumn assignment inside a bulk request.
 *
 * The edge is addressed EITHER by its row `edgeId` (precise; obtained from
 * `GET /v1/workflows/:id/edges`) OR by the `fromNodeKey`/`toNodeKey` pair
 * (convenient; readable straight off the graph snapshot that
 * `get_workflow_structure` returns, which carries no row ids). Exactly one
 * addressing mode per entry — supplying both, or neither, is rejected.
 *
 * The pair form is ambiguous when a workflow has parallel edges between the
 * same two nodes (distinguished only by `condition`); the server answers 422
 * with the candidate edge ids so the caller can retry with `edgeId`.
 * (DEV-17058.)
 */
export const EdgeBoardColumnAssignmentSchema = z
    .object({
    /** Row id from `GET /v1/workflows/:id/edges`. Mutually exclusive with the node-key pair. */
    edgeId: z.string().min(1).optional(),
    /** Source node key. Must be supplied together with `toNodeKey`. */
    fromNodeKey: z.string().min(1).optional(),
    /** Target node key. Must be supplied together with `fromNodeKey`. */
    toNodeKey: z.string().min(1).optional(),
    /** BoardColumn id to attribute this edge to, or `null` to clear it. */
    boardColumnId: z.string().min(1).nullable(),
})
    .refine((a) => (a.edgeId !== undefined) !== (a.fromNodeKey !== undefined && a.toNodeKey !== undefined), {
    message: 'Address the edge either by `edgeId` or by both `fromNodeKey` and `toNodeKey` — not both, not neither.',
});
/**
 * Bulk body for `PATCH /v1/workflows/:id/edges`. Applies every assignment in
 * ONE version cut, unlike N calls to `PATCH .../edges/:edgeId` which cut N
 * versions. Added for the MCP surface (DEV-17058) — an agent retagging a
 * branch conversationally would otherwise litter the version history — but
 * it's a plain REST endpoint the builder can adopt for drag-assign too.
 *
 * All-or-nothing: an unresolvable, ambiguous, or doubly-targeted edge rejects
 * the whole request without cutting a version.
 */
export const AssignEdgeBoardColumnsInputSchema = z.object({
    assignments: z.array(EdgeBoardColumnAssignmentSchema).min(1).max(200),
});
/** Compact (node-ids-only) representation of a workflow graph — what users POST. */
export const WorkflowGraphInputSchema = z.object({
    nodes: z.array(z.object({
        nodeKey: WorkflowNodeSchema.shape.nodeKey,
        nodeType: WorkflowNodeSchema.shape.nodeType,
        config: z.record(z.string(), z.unknown()),
        inputMapping: z.record(z.string(), z.unknown()).default({}),
        outputSchema: z.record(z.string(), z.unknown()),
        /**
         * Optional JSONata expression evaluated on this node's raw return
         * value (with the full `previousOutputs` map in scope) to compose
         * the recorded node output. (DEV-15895 / EU-28.)
         */
        outputMapping: z.string().min(1).optional(),
        position: NodePositionSchema.optional(),
    })),
    edges: z.array(z.object({
        fromNodeKey: z.string(),
        toNodeKey: z.string(),
        condition: z.string().optional(),
        /** See `WorkflowEdgeSchema.boardColumnId`. (DEV-16183.) */
        boardColumnId: z.string().nullable().optional(),
    })),
});
// ─── Workflow + Version ──────────────────────────────────────────────────────
export const WorkflowSchema = z.object({
    id: z.string(),
    orgId: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    workflowType: z.string(),
    /**
     * User-supplied tags for grouping / filtering workflows (and the evals that
     * hang off them) by customer or theme (e.g. `["intel"]`). The Workflow is
     * the source of truth for eval grouping. Normalized server-side (trimmed,
     * lowercased, de-duped, capped). Empty by default.
     */
    tags: z.array(z.string()).default([]),
    currentVersionId: z.string().nullable(),
    /**
     * When set, the workflow is disabled — `POST /v1/runs` rejects new triggers
     * with 409. Reversible via `POST /v1/workflows/:id/enable`.
     * (DEV-15873 / DEV-15874 — EU-07/EU-08.)
     */
    disabledAt: z.date().nullable(),
    /**
     * Formant template that owns this workflow (e.g. `"daily-brief"`); `null`
     * = user-owned. Managed rows refuse DELETE (409), lock the node keys /
     * fields the template names, and refuse public `POST /v1/runs`
     * (`managed-workflow-run-internal`). (DEV-17471.)
     */
    managedBy: z.string().nullable().optional(),
    /**
     * Instance identity inside `managedBy` — `{ orgId, viewId, moduleNodeId,
     * level, viewName }` for daily-brief. Free JSON for the console.
     */
    managedRef: z.record(z.string(), z.unknown()).nullable().optional(),
    /** Template version the current graph was instantiated / upgraded from. */
    templateVersion: z.number().int().nullable().optional(),
    /** Node keys that cannot be removed, renamed, retyped or restructured. */
    lockedNodeKeys: z.array(z.string()).default([]),
    /** nodeKey → read-only config field names; `["*"]` locks the whole config. */
    lockedFields: z.record(z.string(), z.array(z.string())).nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    /**
     * When the most-recent run for this workflow was triggered (its
     * `Run.createdAt`), or `null` if the workflow has never been run. Derived
     * per-request from the `Run` table — not a denormalized column. Lets the
     * list page + MCP tools show "last run" without an N+1 fetch.
     */
    lastRunAt: z.date().nullable(),
    /**
     * Save-time tool-ref validation warnings (DEV-16944). Present (non-empty)
     * only on the response of a save that referenced unknown tool refs —
     * create, graph update, or node patch. Advisory only: the save always
     * succeeds; the runner skips unresolvable refs at execution time.
     * Additive field — absent on reads and on clean saves.
     */
    warnings: z.array(ToolRefWarningSchema).optional(),
});
/**
 * Compact graph shape carried on `GET /v1/workflows` rows so the list page
 * can render a mini preview without an N+1 fetch per workflow. Only the
 * structural fields needed to draw nodes-as-dots + edges-as-lines — no
 * `config`, no `inputMapping`, etc. (DEV-16132.) `null` when the workflow
 * has no current version yet (freshly created, no graph saved).
 */
export const WorkflowGraphSummarySchema = z
    .object({
    nodes: z.array(z.object({
        nodeKey: z.string(),
        nodeType: z.string(),
        position: NodePositionSchema,
    })),
    edges: z.array(z.object({
        fromNodeKey: z.string(),
        toNodeKey: z.string(),
    })),
})
    .nullable();
/**
 * One day's run count for the list-page sparkline. `date` is a UTC calendar
 * day (`YYYY-MM-DD`); `count` is the number of runs triggered that day.
 */
export const WorkflowRunTrendPointSchema = z.object({
    date: z.string(),
    count: z.number().int(),
});
/**
 * `GET /v1/workflows` row shape — a Workflow plus derived flags:
 *  - `hasMissingAuth`: any agent references an MCP connection that's
 *    missing or unauthenticated. (DEV-15859 / EU-58.)
 *  - `graphSummary`: compact node+edge summary for the list page's mini
 *    preview. (DEV-16132.)
 *  - `runTrend`: per-day run counts for the trailing 7 UTC days (oldest →
 *    newest, always length 7, zero-filled), powering the card sparkline.
 */
export const WorkflowListItemSchema = WorkflowSchema.extend({
    hasMissingAuth: z.boolean(),
    graphSummary: WorkflowGraphSummarySchema,
    runTrend: z.array(WorkflowRunTrendPointSchema),
});
export const WorkflowVersionStatusEnum = z.enum(['DRAFT', 'ACTIVE', 'ARCHIVED']);
/**
 * Where a version came from. `USER` = a human authored it in the builder
 * (create / edit / duplicate / patch). `AUTO_REFINEMENT` = the refinement
 * agent created it as a detached candidate. `IMPORT` = a workflow-bundle
 * import. `TEMPLATE` = instantiated or upgraded from a Formant managed
 * template (DEV-17471). Surfaced in the builder's Versions tab.
 */
export const WorkflowVersionSourceEnum = z.enum(['USER', 'AUTO_REFINEMENT', 'IMPORT', 'TEMPLATE']);
export const WorkflowVersionSchema = z.object({
    id: z.string(),
    workflowId: z.string(),
    versionNumber: z.number().int().positive(),
    graph: z.record(z.string(), z.unknown()),
    /**
     * JSON Schema describing the workflow's trigger input — what callers must
     * POST to /v1/runs. `null` means no schema was published with this version
     * (legacy rows or workflows authored without a documented contract).
     */
    inputSchema: z.record(z.string(), z.unknown()).nullable(),
    /**
     * JSON Schema describing the workflow's final output (`Run.output`).
     * `null` means no contract published — the runner falls back to "last
     * terminal node output" and skips validation. (DEV-15894 / EU-27.)
     */
    outputSchema: z.record(z.string(), z.unknown()).nullable(),
    /**
     * JSONata expression evaluated against the full `previousOutputs` map
     * at run completion to compose `Run.output`. `null` means the runner
     * uses the legacy "last terminal node wins" rule. (DEV-15894 / EU-27.)
     */
    outputMapping: z.string().nullable(),
    /**
     * Whether this workflow participates in dedup. When false, runs get no
     * `signalKey` and the Signal Orchestrator ignores them.
     * (DEV-16225.)
     */
    dedupEnabled: z.boolean(),
    /**
     * `{variable}` template resolving the run's `signalKey` from its
     * trigger input (e.g. `alarm:{deviceId}:{alarmCode}`). `null` = no key.
     * Variables must reference `inputSchema` fields (validated at save
     * time). See `interpolateSignalKey` / `validateSignalKeyTemplate`
     * in `signal.ts`. (DEV-16225.)
     */
    signalKeyTemplate: z.string().nullable(),
    /** DRAFT = experimental (not promoted), ACTIVE = promoted, ARCHIVED = retired. */
    status: WorkflowVersionStatusEnum,
    attribution: z.record(z.string(), z.unknown()),
    createdAt: z.date(),
    createdByUserId: z.string().nullable(),
    /** Provenance: where this version came from (see `WorkflowVersionSourceEnum`). */
    source: WorkflowVersionSourceEnum,
    /**
     * Keycloak subject of the human who promoted this version to current —
     * who approved a refinement suggestion or manually promoted an older
     * version. `null` until promoted, and `null` for versions that became
     * current via ordinary authoring rather than an explicit promote.
     */
    promotedBy: z.string().nullable(),
    /** When `promotedBy` promoted this version. `null` when never promoted. */
    promotedAt: z.date().nullable(),
    /**
     * Id of the experiment this version was created for, when it originated
     * from a refinement suggestion. `null` for hand-authored versions. Lets the
     * Versions tab link a version back to the suggestion that produced it.
     */
    experimentId: z.string().nullable(),
});
// ─── Version diff (GET /v1/workflows/:id/versions/:from/diff/:to) ─────────────
/**
 * Canonical snapshot of a node as it existed in one version — the fields the
 * diff compares. Sourced from the authoritative `WorkflowNode` rows (not the
 * `WorkflowVersion.graph` blob, which goes stale on single-node config patches
 * and is empty on bundle imports). Excludes `position` (purely visual) and row
 * ids, which are not part of a semantic graph diff.
 */
export const WorkflowVersionNodeSnapshotSchema = z.object({
    nodeKey: z.string(),
    nodeType: z.string(),
    config: z.record(z.string(), z.unknown()),
    inputMapping: z.record(z.string(), z.unknown()),
    outputSchema: z.record(z.string(), z.unknown()),
    outputMapping: z.string().nullable(),
});
export const WorkflowVersionEdgeSnapshotSchema = z.object({
    fromNodeKey: z.string(),
    toNodeKey: z.string(),
    condition: z.string().nullable(),
    boardColumnId: z.string().nullable(),
});
/** Node fields the diff tracks (everything in the snapshot except `nodeKey`). */
export const WorkflowVersionDiffNodeFieldEnum = z.enum([
    'nodeType',
    'config',
    'inputMapping',
    'outputSchema',
    'outputMapping',
]);
/** Edge fields the diff tracks (matched by the `from->to` pair). */
export const WorkflowVersionDiffEdgeFieldEnum = z.enum(['condition', 'boardColumnId']);
export const WorkflowVersionEndpointSchema = z.object({
    versionId: z.string(),
    versionNumber: z.number().int().positive(),
});
/**
 * Structural diff between two workflow versions, with full before/after
 * snapshots so the client can render a git-diff-style view of the changed
 * node configs (added → all-new lines, removed → all-deleted lines, changed →
 * a line-level diff of the canonical JSON). Nodes are matched by `nodeKey`,
 * edges by their `from->to` pair.
 */
export const WorkflowVersionDiffSchema = z.object({
    workflowId: z.string(),
    from: WorkflowVersionEndpointSchema,
    to: WorkflowVersionEndpointSchema,
    nodes: z.object({
        added: z.array(WorkflowVersionNodeSnapshotSchema),
        removed: z.array(WorkflowVersionNodeSnapshotSchema),
        changed: z.array(z.object({
            nodeKey: z.string(),
            changedFields: z.array(WorkflowVersionDiffNodeFieldEnum),
            before: WorkflowVersionNodeSnapshotSchema,
            after: WorkflowVersionNodeSnapshotSchema,
        })),
    }),
    edges: z.object({
        added: z.array(WorkflowVersionEdgeSnapshotSchema),
        removed: z.array(WorkflowVersionEdgeSnapshotSchema),
        changed: z.array(z.object({
            fromNodeKey: z.string(),
            toNodeKey: z.string(),
            changedFields: z.array(WorkflowVersionDiffEdgeFieldEnum),
            before: WorkflowVersionEdgeSnapshotSchema,
            after: WorkflowVersionEdgeSnapshotSchema,
        })),
    }),
});
// ─── Create / Update inputs ──────────────────────────────────────────────────
export const CreateWorkflowInputSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(1000).optional(),
    workflowType: z.string().min(1).max(100),
    /**
     * Optional grouping tags. Normalized server-side (trim → lowercase → drop
     * empties → dedupe → cap 20). Omit to create an untagged workflow.
     */
    tags: z.array(z.string()).optional(),
    graph: WorkflowGraphInputSchema,
    /**
     * Optional JSON Schema for the trigger input. Surfaced via
     * GET /v1/workflows/:id/input-schema so callers know what to POST.
     * Free-form `record` here — Zod just confirms it's an object.
     */
    inputSchema: z.record(z.string(), z.unknown()).optional(),
    /**
     * Optional JSON Schema describing the workflow's final output. Mirrors
     * `inputSchema`. Validated at run completion. (DEV-15894 / EU-27.)
     */
    outputSchema: z.record(z.string(), z.unknown()).optional(),
    /**
     * Optional JSONata expression that composes `Run.output` from the full
     * `previousOutputs` map at run completion. When omitted, the runner
     * falls back to "last terminal node wins". (DEV-15894 / EU-27.)
     */
    outputMapping: z.string().min(1).optional(),
    /** Whether this workflow participates in dedup (default false). (DEV-16225.) */
    dedupEnabled: z.boolean().optional(),
    /**
     * `{variable}` signal-key template. Variables must reference
     * `inputSchema` fields (validated at save time). (DEV-16225.)
     */
    signalKeyTemplate: z.string().max(500).optional(),
});
export const UpdateWorkflowInputSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(1000).optional(),
    /**
     * Replace the workflow's grouping tags. Normalized server-side (trim →
     * lowercase → drop empties → dedupe → cap 20). Pass `[]` to clear all tags;
     * `undefined` leaves them unchanged. This is a plain field update — it does
     * NOT cut a new version.
     */
    tags: z.array(z.string()).optional(),
    /** Editing the graph auto-creates a new WorkflowVersion. */
    graph: WorkflowGraphInputSchema.optional(),
    /**
     * Updating the input schema cuts a new version (same as editing the
     * graph). Pass `null` to explicitly clear a previously-published schema.
     */
    inputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
    /**
     * Updating the output schema cuts a new version (DEV-15894 / EU-27).
     * Pass `null` to clear; `undefined` leaves the previous version's value.
     */
    outputSchema: z.record(z.string(), z.unknown()).nullable().optional(),
    /**
     * Updating the output mapping cuts a new version (DEV-15894 / EU-27).
     * Pass `null` to clear; `undefined` leaves the previous value.
     */
    outputMapping: z.string().min(1).nullable().optional(),
    /** Toggle dedup participation. `undefined` leaves the previous value. (DEV-16225.) */
    dedupEnabled: z.boolean().optional(),
    /**
     * Update the signal-key template. `null` clears it; `undefined`
     * leaves the previous value. Variables must reference `inputSchema`
     * fields (validated at save time). (DEV-16225.)
     */
    signalKeyTemplate: z.string().max(500).nullable().optional(),
});
/**
 * Body for `POST /v1/workflows/:id/versions` (DEV-16363).
 *
 * Cuts a new WorkflowVersion that does NOT become the workflow's current —
 * a "detached" / "one-off" version that lives off mainline. The new row is
 * persisted with `status: 'DRAFT'` and `attribution.detached: true`. Trigger
 * it by passing the returned `workflowVersionId` to `POST /v1/runs`, or
 * promote it later via `POST /v1/workflows/:id/promote-version`.
 *
 * The graph is validated against the node-type registry exactly like the
 * mainline create/update paths. Schema/mapping fields are optional — when
 * omitted they're inherited from the workflow's current version.
 */
export const CreateDetachedVersionInputSchema = z.object({
    graph: WorkflowGraphInputSchema,
    /** Optional JSON Schema for the trigger input. Omit to inherit from current. */
    inputSchema: z.record(z.string(), z.unknown()).optional(),
    /** Optional JSON Schema for the run output. Omit to inherit from current. */
    outputSchema: z.record(z.string(), z.unknown()).optional(),
    /** Optional JSONata expression composing `Run.output`. Omit to inherit from current. */
    outputMapping: z.string().min(1).optional(),
});
/**
 * Body for `POST /v1/workflows/:id/promote-version` (DEV-15899 / EU-32).
 * The named version becomes the workflow's new `currentVersion`. The version
 * graph is NOT rewritten — only the FK is re-pointed. In-flight runs pin to
 * a specific version at start, so they are unaffected; only NEW runs that
 * don't supply `workflowVersionId` explicitly will use the new current.
 */
export const PromoteWorkflowVersionInputSchema = z
    .object({
    workflowVersionId: z.string().min(1),
})
    .strict();
/**
 * Response shape for `GET /v1/workflows/:id/input-schema`. The schema lives
 * on a `WorkflowVersion`, so we surface which version this is from too —
 * a caller fetching against `currentVersion` may want to record the version
 * it triggered against.
 */
/**
 * Body for `PATCH /v1/workflows/:id/nodes/:nodeKey` (gap B1).
 * Updates a single node's config, inputMapping, and/or outputSchema without
 * re-posting the entire graph. Cuts a new version — copies all nodes/edges
 * from the current version, replacing only the targeted node's fields.
 * At least one of `config`, `inputMapping`, `outputSchema`, `toolRefs`, or
 * `toolAllowlists` must be present.
 */
export const PatchNodeConfigInputSchema = z.object({
    config: z.record(z.string(), z.unknown()).optional(),
    inputMapping: z.record(z.string(), z.unknown()).optional(),
    /** Pass `{}` (empty object) to clear the output schema (switch to free-text). */
    outputSchema: z.record(z.string(), z.unknown()).optional(),
    /**
     * Granular tools patch (DEV-16944): server-side MERGE into the node's
     * `config.toolRefs` — every other config key is preserved. Entries are
     * platform tool ids (from the `@cortex/mcp-tools` registry) or
     * MCPConnection names. Only valid on node types whose config schema has a
     * `toolRefs` field (`llm-agent`) — otherwise the patch is rejected with
     * 400. Unknown refs produce advisory `warnings` on the response; they
     * never block the save.
     */
    toolRefs: z.array(z.string()).optional(),
    /**
     * Granular tools patch companion: per-MCP-connection tool allowlist,
     * merged into `config.toolAllowlists` (replaces the whole map — pass `{}`
     * to clear all allowlists). Keyed by connection name as it appears in
     * `toolRefs`. Same node-type restriction as `toolRefs`.
     */
    toolAllowlists: z.record(z.string(), z.array(z.string())).optional(),
});
/**
 * One entry of the 409 body (`code: 'managed-workflow-locked'`) returned when
 * a PATCH touches a locked part of a Formant-managed workflow or its
 * schedule. `nodeKey` is null for workflow-level settings and schedule
 * violations. (DEV-17471.)
 */
export const ManagedWorkflowLockViolationSchema = z.object({
    nodeKey: z.string().nullable(),
    field: z.string(),
    reason: z.enum([
        'locked-field',
        'locked-node-structure',
        'node-removed',
        'node-added',
        'edges-changed',
        'workflow-settings-locked',
        /** WorkflowSchedule.input / workflowId on a managed workflow's schedule (public API). */
        'schedule-input-locked',
        /**
         * Public create/delete of a schedule on a managed workflow, or a public
         * change to its `name`, `preventOverlap` or `maxConsecutiveFailures`
         * (only cron, timezone and status are editable).
         */
        'schedule-locked',
    ]),
});
/**
 * Body for `PATCH /v1/workflows/:id/versions/:versionId/status` (gap B3).
 * Allows setting a version's status to DRAFT, ACTIVE, or ARCHIVED.
 */
export const UpdateVersionStatusInputSchema = z.object({
    status: WorkflowVersionStatusEnum,
});
/**
 * Body for `POST /v1/workflows/:id/compile-node` — request compilation of one
 * `llm-agent` node into a deterministic `compiled-agent` node (see
 * docs/compiler-plan.md). Creates a Compilation job; the runner generates the
 * code and cuts a detached candidate version. `versionId` pins which version's
 * node to compile — omit to use the workflow's current version.
 */
export const CompileNodeInputSchema = z
    .object({
    nodeKey: z.string().min(1),
    versionId: z.string().min(1).optional(),
})
    .strict();
/**
 * Body for `POST /v1/workflows/:id/compile-subgraph` (DEV-16448) — fuse a
 * connected chain of nodes into ONE `compiled-agent` node. `nodeKeys` must form
 * a linear chain (entry … exit) that is self-contained after its entry; the
 * fused node reuses the exit's key. `versionId` pins the version — omit for
 * current.
 */
export const CompileSubgraphInputSchema = z
    .object({
    nodeKeys: z.array(z.string().min(1)).min(2),
    versionId: z.string().min(1).optional(),
})
    .strict();
export const WorkflowInputSchemaResponseSchema = z.object({
    workflowId: z.string(),
    workflowVersionId: z.string(),
    versionNumber: z.number().int().positive(),
    /** `null` when the version was authored without a published schema. */
    inputSchema: z.record(z.string(), z.unknown()).nullable(),
});
/**
 * Response shape for `GET /v1/workflows/:id/output-schema` (DEV-15894 / EU-27).
 * Mirrors the input-schema response. Both `outputSchema` and `outputMapping`
 * are nullable: a workflow can declare just one or both, or neither (legacy).
 */
export const WorkflowOutputSchemaResponseSchema = z.object({
    workflowId: z.string(),
    workflowVersionId: z.string(),
    versionNumber: z.number().int().positive(),
    /** `null` when the version was authored without a published schema. */
    outputSchema: z.record(z.string(), z.unknown()).nullable(),
    /** `null` when the version was authored without a JSONata composition rule. */
    outputMapping: z.string().nullable(),
});
/**
 * Response shape for `GET /v1/workflows/:id/dedup-settings` (DEV-16225).
 * Mirrors the input/output-schema responses. Surfaces the current version's
 * deduplication settings so the builder can hydrate the Deduplication
 * section without re-deriving them from a full version payload.
 */
export const WorkflowDedupSettingsResponseSchema = z.object({
    workflowId: z.string(),
    workflowVersionId: z.string(),
    versionNumber: z.number().int().positive(),
    /** Whether this version participates in run deduplication. */
    dedupEnabled: z.boolean(),
    /** `{variable}` signal-key template, or `null` when none is set. */
    signalKeyTemplate: z.string().nullable(),
});
// ─── Evaluation ↔ Judge attachment (JudgeBinding) ───────────────────────────
/**
 * Output of `GET /v1/evaluations/:id/judges`. One row per attached Judge.
 * `judgeName` + `judgeDescription` denormalize the Judge row so the console
 * can render without a second round-trip. `enabled=false` rows still show
 * up so users can see attachments they've toggled off.
 *
 * No per-binding input mapping — the judge's own `inputSchema` is the
 * contract. The harness sends `{ output, expected, input }` for
 * eval-targeted runs (`expected` populated from ground truth); judges
 * that don't need `expected` declare it optional in their schema and
 * become reusable in non-eval contexts later.
 */
export const EvaluationJudgeAttachmentSchema = z.object({
    evaluationId: z.string(),
    judgeId: z.string(),
    judgeName: z.string(),
    judgeDescription: z.string().nullable(),
    judgeType: z.enum(['CODE', 'LLM_ONE_SHOT']),
    enabled: z.boolean(),
    createdAt: z.string().datetime(),
});
/**
 * Body for `PUT /v1/evaluations/:id/judges/:judgeId`. Idempotent upsert.
 * `enabled` is the only mutable field.
 */
export const UpsertEvaluationJudgeInputSchema = z.object({
    enabled: z.boolean().optional(),
});
/**
 * One sample for the per-judge score chart on the eval results page. The
 * Y value's shape depends on the score's declared type (boolean | number | enum)
 * — chart components dispatch on `valueType`. `evaluationId` is included so
 * the chart can group/colour by eval when an aggregate view is shown.
 */
export const JudgeScoreSampleSchema = z.object({
    scoreName: z.string(),
    valueType: z.enum(['boolean', 'number', 'enum']),
    value: z.union([z.boolean(), z.number(), z.string()]),
    reason: z.string(),
    createdAt: z.string().datetime(),
    evaluationId: z.string().nullable(),
    judgeRunId: z.string(),
});
/**
 * Response shape for `GET /v1/evaluations/:id/judge-scores`. Grouped by judge
 * for cheap rendering: each judge gets a panel and each score within it gets
 * a chart. `samples` arrives ordered by `createdAt` ascending.
 */
export const EvaluationJudgeScoresResponseSchema = z.object({
    evaluationId: z.string(),
    judges: z.array(z.object({
        judgeId: z.string(),
        judgeName: z.string(),
        samples: z.array(JudgeScoreSampleSchema),
    })),
});
//# sourceMappingURL=workflow.js.map